import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { XIcon } from 'lucide-react';
import { Button, Separator, Skeleton } from '@monksflow/monks-ui';
import { ChatSuggestionsProps } from './ChatSuggestions.types';

const ChatSuggestionLoader = () => (
  <div aria-busy className="flex flex-col gap-0.5" data-testid="chat-suggestions-loader">
    {[0, 1, 2].map((i) => (
      <Skeleton key={i} className="h-4.5 w-full rounded-lg" />
    ))}
  </div>
);

export const ChatSuggestions = ({
  isLoading,
  suggestions,
  handleStarterClick,
  disabled,
  onHide,
}: ChatSuggestionsProps) => {
  const { t } = useTranslation('chats');
  // Show loading state if loading, even with empty suggestions
  if (isLoading) {
    return (
      <div className="p-4">
        <ChatSuggestionLoader />
      </div>
    );
  }

  // Only return null if not loading and no suggestions
  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      {onHide && (
        <Button
          variant="ghost"
          size="sm"
          className="absolute top-2 right-2 h-6 w-6 rounded-md"
          aria-label={t('chats.chatSuggestions.hide')}
          onClick={onHide}
        >
          <XIcon className="h-3.5 w-3.5" />
        </Button>
      )}
      <div className={clsx('flex flex-col gap-0.5 p-2', onHide && 'pr-9')}>
        {suggestions.map((suggestion, index) => (
          <button
            type="button"
            key={index}
            disabled={disabled}
            onClick={() => handleStarterClick(suggestion)}
            className="block rounded-lg px-2 py-1 text-left text-sm leading-6 font-normal text-muted-foreground transition-colors duration-fast ease-out hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:text-muted-foreground disabled:hover:bg-transparent"
          >
            {suggestion}
          </button>
        ))}
      </div>
      {/* Kept in the a11y tree: it is the boundary between the suggestion list and the composer. */}
      <Separator decorative={false} />
    </div>
  );
};
